import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import { useChat } from "./state/useChat";
import { useSession } from "./state/useSession";
import { useWorkingPanel } from "./state/useWorkingPanel";
import { useDataMappingWebSocket } from "./hooks/useDataMappingWebSocket";
import { useAutoMap } from "./hooks/useAutoMap";
import { buildAutoAnalysePrompt } from "./components/autoAnalysePrompt";
import { buildGreetingPrompt } from "./components/greetingPrompt";
import { buildFoundationGreetingPrompt } from "./components/foundationGreetingPrompt";
import { fetchDataFrameList, fetchFoundationCoverage } from "./api/dataMappingClient";
import { useFoundationV2Years } from "hooks/query";
import { ChatPanel } from "./components/ChatPanel";
import { WorkingPanel } from "./components/WorkingPanel";
import { STAGE_COMMIT, STAGE_LIVE, STAGE_WORKING } from "./components/stages";
import { FileUploaderModal } from "./components/FileUploader";
import { FileLibraryPicker } from "./components/FileLibraryPicker";
import { TaskBar } from "./components/TaskBar";

const CHAT_MIN = 360;
const PANEL_MIN = 360;
const DIVIDER_PX = 6;
const STORAGE_KEY = "aiDataMapping.chatWidth";

export function SessionLayout() {
  const { sessionId, questionnaireId, aiIngestTarget, hasFile, sessionKind, foundationYearId } =
    useSession();
  const isFoundation = sessionKind === "foundation_v2";
  const { isOpen, stage, setStage } = useWorkingPanel();
  // The Commit stage drops the chat panel entirely (brief §1: "the AI chat panel isn't shown") — the
  // stage body becomes two columns of its own (Preview + the Commit rail), with nothing to its left.
  // Preview (STAGE_LIVE) renders that same body, so it drops the chat too; the auto-mapping that runs on
  // arrival there reports its progress in the stage body instead (WorkingPanel's MappedStage).
  // Every other stage keeps the normal chat/divider/working-panel grid.
  const showChat = stage !== STAGE_COMMIT && stage !== STAGE_LIVE;
  const { messages } = useChat();
  const { sendMessage, sendAutoMessage } = useDataMappingWebSocket(sessionId);
  // Runs the AI mapping when the user opens Map & review. All of the "should it run?" reasoning lives in
  // the hook; this file only supplies the send and the two events it cannot see (a message the user sent,
  // and the Re-map button).
  const { remap, noteUserSend } = useAutoMap({ sendAutoMessage });

  const uploadDisclosure = useDisclosure();
  const libraryDisclosure = useDisclosure();

  const mainRef = useRef(null);
  const [chatWidth, setChatWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? parseFloat(stored) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });

  const clampChatWidth = useCallback((desired, mainWidth) => {
    const maxChat = Math.max(CHAT_MIN, mainWidth - PANEL_MIN - DIVIDER_PX);
    return Math.max(CHAT_MIN, Math.min(maxChat, desired));
  }, []);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w <= 0) return;
      setChatWidth((prev) => {
        const desired = prev ?? Math.round(w * 0.33);
        return clampChatWidth(desired, w);
      });
    });
    ro.observe(main);
    return () => ro.disconnect();
  }, [clampChatWidth]);

  useEffect(() => {
    if (chatWidth !== null) {
      localStorage.setItem(STORAGE_KEY, String(chatWidth));
    }
  }, [chatWidth]);

  const handleDividerPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      const main = mainRef.current;
      if (!main) return;
      const rect = main.getBoundingClientRect();

      const onMove = (ev) => {
        const desired = ev.clientX - rect.left;
        setChatWidth(clampChatWidth(desired, rect.width));
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [clampChatWidth]
  );

  // A session that already has data opens on the workbench, not on Upload & view. Upload & view is
  // read-only (the composer is closed there), so landing on it with nothing to upload leaves the user in
  // front of a dead chat box for no reason — worst of all for a Foundation Data v2 session, which never
  // has a file at all: its rows are pushed in at creation, so it would ALWAYS start locked.
  //
  // `index.jsx` calls setFile() before rendering this component, so `hasFile` is right on the first render.
  // Keyed on sessionId rather than on mount so it would still be correct if sessions were ever switched in
  // place; dispatching the same stage twice (StrictMode) is idempotent.
  useEffect(() => {
    if (!sessionId) return;
    if (hasFile || sessionKind === "foundation_v2") setStage(STAGE_WORKING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleSend = useCallback(
    (text) => {
      // Tells the mapping hook that a change may be coming, so whatever this turn does to the working data
      // is allowed to mark the mapped output stale. See `noteUserSend`.
      noteUserSend();
      sendMessage(text);
    },
    [sendMessage, noteUserSend]
  );

  // Adding data to the session starts the conversation, rather than leaving the user in front of an
  // empty chat box having to guess what to type immediately after the one action that states their
  // intent unambiguously.
  //
  // Triggered from the upload/import CALLBACK, deliberately not from a `hasFile` transition: index.jsx's
  // loadSession calls setFile() on every resume, so watching that flag would re-analyse on every page
  // reload of any session that already has a file.
  const handleDataAdded = useCallback(
    ({ frames, sourceName }) => {
      const { text, display } = buildAutoAnalysePrompt({
        frames,
        sourceName,
        // A first upload earns a full opening move; a later one is usually "here is one more input for
        // the thing we are already doing", and re-running the full move would talk over a mapping the
        // user is part-way through approving. Assistant messages count too, because an auto-turn
        // produces no user message of its own.
        isFirstTurn: !messages.some((m) => m.role === "user" || m.role === "assistant"),
        target: aiIngestTarget || "",
      });
      sendAutoMessage(text, display);
      // The analysis this just asked for arrives in the chat, and the chat is closed on Upload & view — so
      // move to the workbench, where it lands and where the user can answer it. `useFileUpload` has
      // already called setFile by the time this callback runs, so the tab is enabled when we get there.
      setStage(STAGE_WORKING);
    },
    [messages, aiIngestTarget, sendAutoMessage, setStage]
  );

  // A brand-new session opens on an empty chat, so the assistant opens the conversation: it introduces
  // itself, says what it is going to map the data into, and asks for the file.
  //
  // Sent as an app-initiated turn (`sendAutoMessage`), not as a message from the user. It used to send the
  // literal string "hello" through the user path, which put a black "hello" bubble in the transcript that
  // the user never typed — and the backend stored it that way, so it came back on every reload. What it says
  // is the app's instruction (./components/greetingPrompt), which the chat replaces with a short label.
  //
  // `didGreetRef` is a one-shot guard (mirrors index.jsx's `didBootstrap`) flipped only once the send
  // actually goes out — NOT synchronously before calling it. It has to wait for real success:
  // `useDataMappingWebSocket`'s connect effect tears down and recreates the websocket once under
  // StrictMode's dev-mode double-invoke, which kills the socket this effect's first attempt was waiting to
  // open on. Flipping the guard early "used up" that attempt and left the second effect run (against the
  // real, persisting socket) seeing an already-spent guard — the greeting never sent. `messages.length > 0`
  // in the condition is the second line of defence once it does.
  //
  // Gated on `!hasFile && messages.length === 0` rather than "is this session brand new": a resumed session
  // that happens to have no file and no messages yet is in the exact same "nothing has happened" state, and
  // greeting it is correct, not a re-greet. Once a file exists, `handleDataAdded` above is what starts the
  // conversation instead.
  /*
    Which greeting, and this gate is the whole point of it. Without the `sessionKind` branch a foundation
    session — which has no file and no messages, because its rows are PUSHED in at creation — got the carbon
    greeting, telling the agent it was mapping into "a carbon data task — you do not know which part of it
    yet", to ask for a file, and not to call any tools. The first two are wrong and the second contradicts
    FOUNDATION_SYSTEM_PROMPT in as many words.
  */
  const { data: foundationYears } = useFoundationV2Years({ enabled: isFoundation });
  const dataYear = isFoundation
    ? (foundationYears || []).find((y) => String(y.id) === String(foundationYearId))
    : null;

  /*
    Coverage for the greeting, resolved in its OWN effect so the greeting effect can stay synchronous.

    That matters more than it looks. `didGreetRef` is deliberately flipped inside `sendAutoMessage`'s
    callback rather than before the call — see the comment above the greeting effect: flipping it early
    "uses up" the attempt StrictMode's double-invoke kills, and the greeting then never sends at all. But
    that also means the guard is not yet set while anything is awaited, so awaiting a fetch *inside* the
    greeting effect would let both invocations reach `sendAutoMessage` and send two greetings.

    So the fetch lives here and publishes a result; `greetingCoverage` is `undefined` until resolved and
    `null` when it could not be read, which is what lets the greeting distinguish "not ready yet" from
    "there is no number to state".

    **There is deliberately no once-only ref here, and that is not an oversight.** A ref claimed before the
    await is the bug this file already documents for `didGreetRef`: StrictMode runs effect → cleanup → effect,
    so the first invocation would claim the slot and then have its result discarded by its own cleanup, while
    the second returns early and never fetches. `greetingCoverage` would sit at `undefined` for the life of
    the session and the greeting — gated on it — would never send at all. `cancelled` alone is the guard; the
    cost is one duplicate GET on the StrictMode remount, which is what `FoundationCoverageStrip` also accepts.
    The effect cannot loop: it is gated on `messages.length === 0`, and the greeting it feeds ends that.
  */
  const [greetingCoverage, setGreetingCoverage] = useState(undefined);
  useEffect(() => {
    if (!isFoundation) return undefined;
    if (!sessionId || hasFile || messages.length > 0) return undefined;

    let cancelled = false;
    (async () => {
      let coverage = null;
      try {
        const frames = await fetchDataFrameList(sessionId);
        // The working frame, not the wanted-datapoints one: `readonly` marks the reference list, and the
        // extracted rows carry the `foundation_v2` origin the push gave them.
        const working = (frames || []).find((f) => !f.readonly && f.origin === "foundation_v2");
        if (working) coverage = await fetchFoundationCoverage(sessionId, working.key);
      } catch (err) {
        // Degrades to a greeting with no numbers. A session with no opening turn at all would be worse
        // than one that does not quantify itself.
        console.warn("could not read coverage for the greeting", err);
      }
      if (!cancelled) setGreetingCoverage(coverage);
    })();
    return () => {
      cancelled = true;
    };
  }, [isFoundation, sessionId, hasFile, messages.length]);

  const didGreetRef = useRef(false);
  useEffect(() => {
    if (didGreetRef.current) return;
    if (!sessionId || hasFile || messages.length > 0) return;
    if (isFoundation) {
      // Wait for the year row, so the greeting can name the workstream, and for coverage to settle either
      // way — `undefined` means still resolving, `null` means it could not be read and we greet anyway.
      if (!dataYear || greetingCoverage === undefined) return;
    }

    const { text, display } = isFoundation
      ? buildFoundationGreetingPrompt({
          year: dataYear?.year ?? null,
          workstream: dataYear?.questionGroup ?? null,
          coverage: greetingCoverage,
        })
      : buildGreetingPrompt({ target: aiIngestTarget || "" });
    sendAutoMessage(text, display, () => {
      didGreetRef.current = true;
    });
  }, [
    sessionId,
    hasFile,
    messages.length,
    aiIngestTarget,
    sendAutoMessage,
    isFoundation,
    dataYear,
    greetingCoverage,
  ]);

  return (
    // 71px matches Layout.jsx's navbar spacer exactly (`<Box h="71px" />`) — using any other value here
    // leaves this Flex taller than the space #layoutContainer actually has, which is what put a second,
    // barely-scrollable scrollbar on the outer page on top of this panel's own internal one.
    <Flex direction="column" h="calc(100vh - 71px)" w="100%" bg="white">
      {/* Which task this writes into, and the way back to it. Above the split so it reads as a property of
          the page rather than of either panel. */}
      <TaskBar />

      {/* Main */}
      <Box ref={mainRef} flex="1" minH={0}>
        <Box
          h="100%"
          display="grid"
          gridTemplateColumns={
            showChat && isOpen && chatWidth !== null
              ? `${chatWidth}px ${DIVIDER_PX}px 1fr`
              : "1fr"
          }
        >
          {showChat && (
            <Box
              display="flex"
              flexDirection="column"
              minH={0}
              minW={0}
              maxW={isOpen ? undefined : "640px"}
              mx={isOpen ? undefined : "auto"}
              w="100%"
              bg="gray.50"
            >
              <ChatPanel onSend={handleSend} />
            </Box>
          )}
          {showChat && isOpen && sessionId && (
            <Box
              onPointerDown={handleDividerPointerDown}
              cursor="col-resize"
              bg="transparent"
              userSelect="none"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize chat and working panel"
            />
          )}
          {(!showChat || isOpen) && sessionId && (
            <Box display="flex" flexDirection="column" minH={0} minW={0} bg="white" overflow="hidden">
              <WorkingPanel
                sessionId={sessionId}
                onOpenUpload={uploadDisclosure.onOpen}
                onOpenLibrary={libraryDisclosure.onOpen}
                onUploaded={handleDataAdded}
                onRemap={remap}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Modals */}
      <FileUploaderModal
        isOpen={uploadDisclosure.isOpen}
        onClose={uploadDisclosure.onClose}
        sessionId={sessionId}
        onUploaded={handleDataAdded}
      />
      <FileLibraryPicker
        isOpen={libraryDisclosure.isOpen}
        onClose={libraryDisclosure.onClose}
        questionnaireId={questionnaireId}
      />
    </Flex>
  );
}
